// Turns a PromiseRejectionEvent's `reason` into something a triage session can
// act on.
//
// This used to be `String(e.reason)`, which collapses the two most common
// non-Error rejections into dead ends: a bare `Promise.reject()` records the
// literal message "undefined" and any plain object records "[object Object]".
// Either way the row reaches site_events carrying a message, a path, and no
// other signal — no stack, nothing to grep for — so it can never be triaged,
// while still ranking on the top-errors list. Returning null drops those events
// instead of recording an untriageable row.

const MAX_MESSAGE_LEN = 200

interface RejectionReport {
  message: string
  stack?: string
}

// Error-shaped without being an Error: cross-realm errors (an Error from an
// iframe fails `instanceof`), and library/SDK objects that carry their own
// `name`/`message`.
const fromObject = (reason: object): string | null => {
  const { message, name } = reason as { message?: unknown; name?: unknown }
  if (typeof message === 'string' && message !== '') {
    return typeof name === 'string' && name !== '' ? `${name}: ${message}` : message
  }
  try {
    const json = JSON.stringify(reason)
    // "{}" is what `[object Object]` was — still nothing to act on.
    if (json && json !== '{}') {
      return json
    }
  } catch {
    // Circular or otherwise non-serializable — fall through to the name.
  }
  return typeof name === 'string' && name !== '' ? name : null
}

export const describeRejection = (reason: unknown): RejectionReport | null => {
  if (reason instanceof Error) {
    // An Error thrown with no message still has a useful constructor name.
    const message = reason.message || reason.name
    if (!message) {
      return null
    }
    return { message: message.slice(0, MAX_MESSAGE_LEN), stack: reason.stack }
  }
  if (reason === null || reason === undefined) {
    return null
  }
  const described = typeof reason === 'object' ? fromObject(reason) : String(reason)
  if (!described) {
    return null
  }
  return { message: described.slice(0, MAX_MESSAGE_LEN) }
}
